import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  TextField,
  InputAdornment,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Divider,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
  Tab,
  Tabs,
  LinearProgress
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import DownloadIcon from "@mui/icons-material/Download";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CloseIcon from "@mui/icons-material/Close";
import BusinessIcon from "@mui/icons-material/Business";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import MeetingRoomIcon from "@mui/icons-material/MeetingRoom";
import MeetingRoomOutlinedIcon from "@mui/icons-material/MeetingRoomOutlined";
import WarehouseIcon from "@mui/icons-material/Warehouse";
import BarChartIcon from "@mui/icons-material/BarChart";
import PieChartIcon from "@mui/icons-material/PieChart";
import StraightenIcon from "@mui/icons-material/Straighten";
import AgricultureIcon from "@mui/icons-material/Agriculture";
import { format } from "date-fns";
import { ExecutePostWithParams, GetDataFromApiWithParams } from "../../../../../services/api/ExecuteApiRequests";

// Helper function to convert any value to number
const toNumber = (value) => {
  if (typeof value === 'number' && !isNaN(value)) return value;
  if (typeof value === 'string') {
    const num = parseFloat(value);
    return isNaN(num) ? 0 : num;
  }
  if (typeof value === 'object' && value !== null) {
    const num = parseFloat(value.toString());
    return isNaN(num) ? 0 : num;
  }
  return 0;
};

// Format number with commas
const formatNumber = (number) => {
  const num = toNumber(number);
  if (num === 0) return "0";
  return new Intl.NumberFormat('en-US').format(num);
};

// Format percentage
const formatPercentage = (value) => {
  const num = toNumber(value);
  if (num === 0) return "0%";
  return `${num.toFixed(1)}%`;
};

// Get color based on percentage value
const getProgressColor = (value) => {
  const num = toNumber(value);
  if (num >= 80) return "#4caf50";
  if (num >= 60) return "#2196f3";
  if (num >= 40) return "#ff9800";
  if (num >= 20) return "#ffc107";
  return "#f44336";
};

// Progress Bar Component for table cells
const ProgressBarCell = ({ value, label, actualValue, color }) => {
  const percentage = Math.min(100, Math.max(0, toNumber(value)));
  const barColor = color || getProgressColor(percentage);
  
  return (
    <Box sx={{ minWidth: 120 }}>
      <Tooltip title={`${label}: ${formatPercentage(percentage)}`} arrow>
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: barColor, fontWeight: 'bold' }}>
              {formatPercentage(percentage)}
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={percentage}
            sx={{
              height: 8,
              borderRadius: 4,
              backgroundColor: '#e0e0e0',
              '& .MuiLinearProgress-bar': {
                backgroundColor: barColor,
                borderRadius: 4,
              }
            }}
          />
        </Box>
      </Tooltip>
    </Box>
  );
};

// Tab Panel Component
const TabPanel = ({ children, value, index, ...other }) => {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`occupancy-tabpanel-${index}`}
      aria-labelledby={`occupancy-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
};

// Gauge Chart Component
const GaugeChart = ({ title, value, color }) => {
  const percentage = Math.min(100, Math.max(0, toNumber(value)));
  
  return (
    <Box sx={{ textAlign: 'center', p: 2 }}>
      <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold', color: '#1A237E' }}>
        {title}
      </Typography>
      <Box sx={{ position: 'relative', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress
          variant="determinate"
          value={100}
          size={130}
          thickness={6}
          sx={{ color: '#e0e0e0', position: 'absolute' }}
        />
        <CircularProgress
          variant="determinate"
          value={percentage}
          size={130}
          thickness={6}
          sx={{
            color: color,
            '& .MuiCircularProgress-circle': {
              strokeLinecap: 'round',
            },
          }}
        />
        <Box
          sx={{
            top: 0,
            left: 0,
            bottom: 0,
            right: 0,
            position: 'absolute',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Typography variant="h5" component="div" sx={{ fontWeight: 'bold', color: color }}>
            {formatPercentage(percentage)}
          </Typography>
        </Box>
      </Box>
      <Typography variant="caption" sx={{ display: 'block', mt: 1, color: '#666' }}>
        {title}
      </Typography>
    </Box>
  );
};

// Bar Chart Component
const BarChart = ({ title, data, dataKey, color, formatValue }) => {
  const items = Object.keys(data);
  
  if (items.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="body2" color="textSecondary">No data available</Typography>
      </Box>
    );
  }
  
  const maxValue = Math.max(...Object.values(data).map(d => toNumber(d[dataKey])), 0);
  
  if (maxValue === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="body2" color="textSecondary">No data available for {title}</Typography>
      </Box>
    );
  }
  
  const barWidth = Math.min(50, Math.max(30, 80 - items.length * 2));
  
  return (
    <Box sx={{ p: 2, overflowX: 'auto' }}>
      <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold', color: '#1A237E', mb: 3, textAlign: 'center' }}>
        {title}
      </Typography>
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'flex-end', 
        gap: 2.5, 
        minHeight: 400, 
        justifyContent: 'center', 
        flexWrap: 'wrap',
        padding: 2
      }}>
        {items.map((item) => {
          const value = toNumber(data[item][dataKey]);
          const height = maxValue > 0 ? (value / maxValue) * 280 : 0;
          const percentage = maxValue > 0 ? ((value / maxValue) * 100).toFixed(1) : 0;
          
          return (
            <Tooltip 
              key={item} 
              title={`${item}: ${formatValue(value)} (${percentage}% of max)`} 
              arrow 
              placement="top"
            >
              <Box sx={{ 
                textAlign: 'center', 
                minWidth: 70,
                maxWidth: 120,
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                '&:hover': {
                  transform: 'translateY(-5px)'
                }
              }}>
                <Box sx={{ 
                  position: 'relative',
                  height: height,
                  width: `${barWidth}px`,
                  bgcolor: color,
                  borderRadius: '4px 4px 0 0',
                  margin: '0 auto',
                  transition: 'all 0.3s ease',
                  opacity: 0.85,
                  '&:hover': {
                    opacity: 1,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
                  }
                }}>
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      position: 'absolute', 
                      top: -22, 
                      left: '50%', 
                      transform: 'translateX(-50%)',
                      fontSize: '10px',
                      fontWeight: 'bold',
                      color: color,
                      whiteSpace: 'nowrap',
                      display: height > 35 ? 'none' : 'block'
                    }}
                  >
                    {formatValue(value)}
                  </Typography>
                </Box>
                
                {height > 35 && (
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      display: 'block',
                      fontSize: '10px',
                      fontWeight: 'bold',
                      color: 'white',
                      mt: -2.5,
                      mb: 0.5,
                      position: 'relative',
                      zIndex: 1,
                      textShadow: '0px 0px 2px rgba(0,0,0,0.5)'
                    }}
                  >
                    {formatValue(value)}
                  </Typography>
                )}
                
                <Typography 
                  variant="body2" 
                  sx={{ 
                    mt: 1.5, 
                    fontWeight: 'bold',
                    fontSize: '0.75rem',
                    maxWidth: '100px',
                    wordWrap: 'break-word',
                    lineHeight: 1.2,
                    color: '#333'
                  }}
                >
                  {item.length > 20 ? item.substring(0, 18) + '...' : item}
                </Typography>
                
                <Typography 
                  variant="caption" 
                  sx={{ 
                    display: 'block', 
                    color: '#888',
                    fontSize: '0.65rem',
                    mt: 0.25
                  }}
                >
                  ({percentage}%)
                </Typography>
              </Box>
            </Tooltip>
          );
        })}
      </Box>
      
      <Box sx={{ 
        width: '100%', 
        bgcolor: '#f8f9fa', 
        mt: 3, 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        p: 2, 
        borderRadius: 2,
        border: '1px solid #e0e0e0'
      }}>
        <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.75rem' }}>
          Total {title}: {formatValue(Object.values(data).reduce((sum, d) => sum + toNumber(d[dataKey]), 0))}
        </Typography>
      </Box>
    </Box>
  );
};

// Pie Chart Component
const PieChart = ({ title, data, formatValue }) => {
  const items = Object.keys(data);
  const total = items.reduce((sum, key) => sum + toNumber(data[key]), 0);
  const colors = ['#4caf50', '#ff9800', '#e91e63', '#2196f3', '#9c27b0', '#d32f2f', '#0288d1', '#7b1fa2', '#e65100', '#c2185b', '#00695c', '#f44336'];
  
  if (items.length === 0 || total === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="body2" color="textSecondary">No data available for the selected filters</Typography>
      </Box>
    );
  }
  
  let startAngle = 0;
  const centerX = 120, centerY = 120, radius = 90;
  const svgSize = 280;
  
  const getPercentage = (value) => {
    return total > 0 ? ((toNumber(value) / total) * 100).toFixed(1) : 0;
  };
  
  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold', color: '#1A237E', mb: 2, textAlign: 'center' }}>
        {title} - Total: {formatValue(total)}
      </Typography>
      
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, justifyContent: 'center', alignItems: 'center' }}>
        <Box sx={{ position: 'relative', width: svgSize, height: svgSize }}>
          <svg width={svgSize} height={svgSize} viewBox={`0 0 ${svgSize} ${svgSize}`}>
            {items.map((key, index) => {
              const value = toNumber(data[key]);
              const percentage = total > 0 ? (value / total) * 100 : 0;
              const angle = (percentage / 100) * 360;
              const endAngle = startAngle + angle;
              
              const startRad = (startAngle * Math.PI) / 180;
              const endRad = (endAngle * Math.PI) / 180;
              
              const x1 = centerX + radius * Math.cos(startRad);
              const y1 = centerY + radius * Math.sin(startRad);
              const x2 = centerX + radius * Math.cos(endRad);
              const y2 = centerY + radius * Math.sin(endRad);
              
              const largeArcFlag = angle > 180 ? 1 : 0;
              const pathData = `M ${centerX} ${centerY} L ${x1} ${y1} A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2} Z`;
              
              const slice = (
                <path 
                  key={key} 
                  d={pathData} 
                  fill={colors[index % colors.length]} 
                  stroke="white" 
                  strokeWidth="2"
                  style={{ cursor: 'pointer', transition: 'opacity 0.3s' }}
                  onMouseEnter={(e) => e.target.style.opacity = '0.8'}
                  onMouseLeave={(e) => e.target.style.opacity = '1'}
                />
              );
              startAngle = endAngle;
              return slice;
            })}
            
            <circle cx={centerX} cy={centerY} r="25" fill="white" />
            <text x={centerX} y={centerY - 5} textAnchor="middle" fontSize="11" fontWeight="bold" fill="#333">Total</text>
            <text x={centerX} y={centerY + 12} textAnchor="middle" fontSize="12" fontWeight="bold" fill="#1976d2">{formatValue(total)}</text>
          </svg>
        </Box>
        
        <Box sx={{ minWidth: 200, maxHeight: 250, overflowY: 'auto' }}>
          {items.map((key, index) => {
            const value = toNumber(data[key]);
            const percentage = getPercentage(value);
            return (
              <Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Box sx={{ width: 12, height: 12, bgcolor: colors[index % colors.length], borderRadius: 1 }} />
                <Typography variant="caption" sx={{ flex: 1 }}>
                  {key.length > 20 ? key.substring(0, 18) + '...' : key} ({percentage}%)
                </Typography>
                <Typography variant="caption" fontWeight="bold" sx={{ color: colors[index % colors.length] }}>
                  {formatValue(value)}
                </Typography>
              </Box>
            );
          })}
        </Box>
      </Box>
      
      <Box sx={{ width: '100%', bgcolor: '#f5f5f5', mt: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', p: 2, borderRadius: 1 }}>
        <Typography variant="body2" color="textSecondary">Total Items: {items.length}</Typography>
        <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5 }}>Total Value: {formatValue(total)}</Typography>
      </Box>
    </Box>
  );
};

// Main Occupancy Report Component
const OccupancyReportDashboard = () => {
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedPark, setSelectedPark] = useState("all");
  const [showChart, setShowChart] = useState(false);
  const [chartTab, setChartTab] = useState(0);
  const [chartType, setChartType] = useState('bar');
  
  const [parksData, setParksData] = useState([]);
  const [parcelsData, setParcelsData] = useState([]);
  const [buildingsData, setBuildingsData] = useState([]);
  const [shedsData, setShedsData] = useState([]);
  const [greenAreasData, setGreenAreasData] = useState([]);
  const [roadsData, setRoadsData] = useState([]);
  const [landLeasedData, setLandLeasedData] = useState([]);
  const [filteredParks, setFilteredParks] = useState([]);
  const [parksList, setParksList] = useState(["all"]);
  const [openDetailsDialog, setOpenDetailsDialog] = useState(false);
  const [selectedParkDetail, setSelectedParkDetail] = useState(null);
  const [buildingPage, setBuildingPage] = useState(0);
  const [buildingRowsPerPage, setBuildingRowsPerPage] = useState(5);

  // State for leased land metrics from API
  const [leasedLandStats, setLeasedLandStats] = useState({
    totalLeasedLand: 0,
    occupiedLeasedLand: 0,
    landLeaseRatio: 0
  });

  // Calculate total green area for a park from infra_green_area table
  const calculateGreenAreaTotal = useCallback((parkId) => {
    if (!greenAreasData || !Array.isArray(greenAreasData)) return 0;
    
    return greenAreasData
      .filter(item => toNumber(item.park_id) === toNumber(parkId))
      .reduce((sum, item) => {
        let area = toNumber(item.capacity || item.area || 0);
        return sum + area;
      }, 0);
  }, [greenAreasData]);

  // Calculate total road area for a park from infra_road table
  const calculateRoadAreaTotal = useCallback((parkId) => {
    if (!roadsData || !Array.isArray(roadsData)) return 0;
    
    return roadsData
      .filter(item => toNumber(item.park_id) === toNumber(parkId))
      .reduce((sum, item) => {
        let area = toNumber(item.capacity || item.area || 0);
        if (area === 0 && item.width && item.geom) {
          area = toNumber(item.width) * 100;
        }
        return sum + area;
      }, 0);
  }, [roadsData]);

  // Calculate leased land statistics using landLeasedData from API
  const calculateLeasedLandStats = useCallback(() => {
    if (!landLeasedData || landLeasedData.length === 0) {
      setLeasedLandStats({
        totalLeasedLand: 0,
        occupiedLeasedLand: 0,
        landLeaseRatio: 0
      });
      return;
    }

    // Calculate total leased land area from the API response
    let totalLeasedLandArea = 0;
    let occupiedLeasedLandArea = 0;

    landLeasedData.forEach(item => {
      // Find matching parcel using upin
      const parcel = parcelsData.find(p => p.upin === item.upin);
      const parcelArea = parcel ? toNumber(parcel.area || 0) : 0;
      
      totalLeasedLandArea += parcelArea;
      
      // Check if the parcel is occupied (parcel_state = 'Occupied')
      if (item.parcel_state === 'Occupied') {
        occupiedLeasedLandArea += parcelArea;
      }
    });
    
    // Calculate land lease ratio
    const landLeaseRatio = totalLeasedLandArea > 0 ? (occupiedLeasedLandArea / totalLeasedLandArea) * 100 : 0;
    
    setLeasedLandStats({
      totalLeasedLand: totalLeasedLandArea,
      occupiedLeasedLand: occupiedLeasedLandArea,
      landLeaseRatio: landLeaseRatio
    });
  }, [landLeasedData, parcelsData]);

  // Initialize with data from APIs
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [parksummary, parcelsummary, buildingsummary, shedsummary, greenAreaData, roadData, leasedland] = await Promise.all([
          GetDataFromApiWithParams(`parks/parksummary`),
          GetDataFromApiWithParams(`parks/parcelsummary`),
          GetDataFromApiWithParams(`parks/buildingsummary`),
          GetDataFromApiWithParams(`parks/shedsummary`),
          GetDataFromApiWithParams(`parks/greenareasummary`),
          GetDataFromApiWithParams(`parks/roadsummary`),
          GetDataFromApiWithParams(`parks/landleasedsummary`)
        ]);
        
        console.log("Park API Response:", parksummary);
        console.log("Green Area API Response:", greenAreaData);
        console.log("Road API Response:", roadData);
        console.log("Land Leased API Response:", leasedland);
        
        setParksData(parksummary || []);
        setFilteredParks(parksummary || []);
        setGreenAreasData(greenAreaData || []);
        setRoadsData(roadData || []);
        setLandLeasedData(leasedland || []);
        
        // Process building data
        const processedBuildings = (buildingsummary || []).map(building => {
          let floorArea = toNumber(building.floor_area || building.area || building.building_area || 0);
          
          if (floorArea === 0 && building.no_of_floors) {
            floorArea = toNumber(building.no_of_floors) * 500;
          }
          
          let occupancyRatio = toNumber(building.occupancy_ratio || building.occupancy_rate || 0);
          
          return {
            id: building.id,
            park_id: parseInt(building.park_id),
            status: building.status || "Active",
            name: building.name || `Building ${building.id}`,
            bldg_no: building.bldg_no || building.block_id || `B-${building.id}`,
            no_of_floors: toNumber(building.no_of_floors || 1),
            no_of_units: toNumber(building.no_of_units || 0),
            floor_area_m2: floorArea,
            occupancy_ratio: occupancyRatio
          };
        });
        
        // Process sheds data
        const processedSheds = (shedsummary || []).map(shed => ({
          id: shed.id,
          park_id: toNumber(shed.park_id),
          upin: shed.upin || null,
          status_utilization: shed.status_utilization || "Vacant",
          shed_size: toNumber(shed.shed_size || shed.area || 0)
        }));
        
        console.log("Processed Buildings:", processedBuildings);
        console.log("Processed Sheds:", processedSheds);
        setBuildingsData(processedBuildings);
        setShedsData(processedSheds);
        setParcelsData(parcelsummary || []);
        
        const uniqueParks = [...new Set((parksummary || []).map(p => p.name))];
        setParksList(["all", ...uniqueParks]);
      } catch (error) {
        console.error("Failed to fetch data:", error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);

  // Call leased land calculation when data loads
  useEffect(() => {
    if (landLeasedData.length > 0 && parcelsData.length > 0) {
      calculateLeasedLandStats();
    }
  }, [landLeasedData, parcelsData, calculateLeasedLandStats]);

  // Calculate park-specific leased land for a given park
  const getParkLeasedLandStats = useCallback((parkId) => {
    if (!landLeasedData.length || !parcelsData.length) return { leasedLand: 0, occupiedLeasedLand: 0, ratio: 0 };
    
    let parkLeasedLand = 0;
    let parkOccupiedLeasedLand = 0;
    
    landLeasedData.forEach(item => {
      const parcel = parcelsData.find(p => p.upin === item.upin && toNumber(p.park_id) === toNumber(parkId));
      if (parcel) {
        const parcelArea = toNumber(parcel.area || 0);
        parkLeasedLand += parcelArea;
        if (item.parcel_state === 'Occupied') {
          parkOccupiedLeasedLand += parcelArea;
        }
      }
    });
    
    const ratio = parkLeasedLand > 0 ? (parkOccupiedLeasedLand / parkLeasedLand) * 100 : 0;
    return { leasedLand: parkLeasedLand, occupiedLeasedLand: parkOccupiedLeasedLand, ratio };
  }, [landLeasedData, parcelsData]);

  // Get buildings for a specific park
  const getBuildingsForPark = useCallback((parkId) => {
    return buildingsData.filter(building => building.park_id === toNumber(parkId));
  }, [buildingsData]);

  // Calculate park metrics with area sizes including green and road areas
  const parkMetrics = useMemo(() => {
    const metrics = {};
    parksData.forEach(park => {
      const parkId = park.id;
      const parkArea = toNumber(park.area_on_plan || 0);
      const parkAreaM2 = parkArea;
      const parkParcels = parcelsData.filter(p => toNumber(p.park_id) === toNumber(parkId));
      
      // Calculate area by type from parcels
      let totalParcelArea = 0;
      let industrialParcelArea = 0;
      let commercialArea = 0;
      
      parkParcels.forEach(parcel => {
        const area = toNumber(parcel.area || 0);
        totalParcelArea += area;
        if (parcel.type === "Industrial") industrialParcelArea += area;
        else if (parcel.type === "Commercial") commercialArea += area;
      });
      
      // Get green area from infra_green_area table
      const greenArea = calculateGreenAreaTotal(parkId);
     
      // Get road area from infra_road table
      const roadArea = calculateRoadAreaTotal(parkId);
      
      const parkBuildings = getBuildingsForPark(parkId);
      
      // Calculate building area
      const totalBuildingArea = parkBuildings.reduce((sum, b) => sum + toNumber(b.floor_area_m2), 0);
      const activeBuildingArea = parkBuildings
        .filter(b => b.status === "Active")
        .reduce((sum, b) => sum + toNumber(b.floor_area_m2), 0);
      
      const parkSheds = shedsData.filter(s => s.park_id === toNumber(parkId));
      const totalShedArea = parkSheds.reduce((sum, s) => sum + toNumber(s.shed_size), 0);
      const occupiedShedArea = parkSheds
        .filter(s => s.status_utilization === "Occupied")
        .reduce((sum, s) => sum + toNumber(s.shed_size), 0);
      
      const occupiedIndustrialArea = parkParcels
        .filter(p => (p.occupancy_status === "Occupied" || p.status === "Leased") && p.type === "Industrial")
        .reduce((sum, p) => sum + toNumber(p.area), 0);
      
      const occupiedParcels = parkParcels.filter(p => p.occupancy_status === "Occupied" || p.status === "Leased").length;
      const occupiedArea = parkParcels
        .filter(p => p.occupancy_status === "Occupied" || p.status === "Leased")
        .reduce((sum, p) => sum + toNumber(p.area), 0);
      
      // Calculate ratios based on park area in m²
      const occupancyRate = parkAreaM2 > 0 ? (occupiedArea / parkAreaM2) * 100 : 0;
      const parcelRatio = parkParcels.length > 0 ? (occupiedParcels / parkParcels.length) * 100 : 0;
      
      const activeBuildings = parkBuildings.filter(b => b.status === "Active").length;
      const buildingRatio = parkBuildings.length > 0 ? (activeBuildings / parkBuildings.length) * 100 : 0;
      
      const occupiedSheds = parkSheds.filter(s => s.status_utilization === "Occupied").length;
      const shedOccupancyRate = parkSheds.length > 0 ? (occupiedSheds / parkSheds.length) * 100 : 0;
      
      // Calculate green and road ratios based on park area
      const greenAreaRatio = parkAreaM2 > 0 ? (greenArea / parkAreaM2) * 100 : 0;
      const roadRatio = parkAreaM2 > 0 ? (roadArea / parkAreaM2) * 100 : 0;
      
      // Get park-specific leased land stats
      const parkLeasedStats = getParkLeasedLandStats(parkId);
      
      metrics[park.name] = {
        park, 
        totalArea: parkArea,
        totalAreaM2: parkAreaM2,
        totalParcelArea,
        occupiedArea, 
        occupancyRate, 
        totalParcels: parkParcels.length, 
        occupiedParcels, 
        parcelRatio,
        totalBuildings: parkBuildings.length, 
        activeBuildings, 
        buildingRatio,
        totalSheds: parkSheds.length, 
        occupiedSheds, 
        shedOccupancyRate,

        greenAreaRatio, 
        roadRatio,
        industrialParcelArea,
        occupiedIndustrialArea,
        totalBuildingArea,
        activeBuildingArea,
        totalShedArea,
        occupiedShedArea,
        greenArea,
        roadArea,
        commercialArea,
        // Add leased land metrics to park metrics
        totalLeasedLand: parkLeasedStats.leasedLand,
        occupiedLeasedLand: parkLeasedStats.occupiedLeasedLand,
        landLeaseRatio: parkLeasedStats.ratio
      };
    });
    return metrics;
  }, [parksData, parcelsData, getBuildingsForPark, shedsData, calculateGreenAreaTotal, calculateRoadAreaTotal, getParkLeasedLandStats]);

  // Calculate summary statistics including leased land
  const summaryStats = useMemo(() => {
    const totalParkArea = parksData.reduce((sum, p) => sum + toNumber(p.area_on_plan), 0);
    const totalParkAreaM2 = totalParkArea;
    const totalParcelArea = Object.values(parkMetrics).reduce((sum, m) => sum + toNumber(m.totalParcelArea), 0);
    const totalOccupiedArea = Object.values(parkMetrics).reduce((sum, m) => sum + toNumber(m.occupiedArea), 0);
    const overallOccupancyRate = totalParkAreaM2 > 0 ? (totalOccupiedArea / totalParkAreaM2) * 100 : 0;
    const totalParcels = Object.values(parkMetrics).reduce((sum, m) => sum + toNumber(m.totalParcels), 0);
    const totalOccupiedParcels = Object.values(parkMetrics).reduce((sum, m) => sum + toNumber(m.occupiedParcels), 0);
    const overallParcelOccupancy = totalParcels > 0 ? (totalOccupiedParcels / totalParcels) * 100 : 0;
    const totalBuildings = Object.values(parkMetrics).reduce((sum, m) => sum + toNumber(m.totalBuildings), 0);
    const totalActiveBuildings = Object.values(parkMetrics).reduce((sum, m) => sum + toNumber(m.activeBuildings), 0);
    const overallBuildingUtilization = totalBuildings > 0 ? (totalActiveBuildings / totalBuildings) * 100 : 0;
    
    const totalGreenArea = Object.values(parkMetrics).reduce((sum, m) => sum + toNumber(m.greenArea), 0);
    const totalRoadArea = Object.values(parkMetrics).reduce((sum, m) => sum + toNumber(m.roadArea), 0);
    const avgGreenAreaRatio = totalParkAreaM2 > 0 ? (totalGreenArea / totalParkAreaM2) * 100 : 0;
    const avgRoadRatio = totalParkAreaM2 > 0 ? (totalRoadArea / totalParkAreaM2) * 100 : 0;
    
    const totalSheds = Object.values(parkMetrics).reduce((sum, m) => sum + toNumber(m.totalSheds), 0);
    const totalOccupiedSheds = Object.values(parkMetrics).reduce((sum, m) => sum + toNumber(m.occupiedSheds), 0);
    const overallShedOccupancy = totalSheds > 0 ? (totalOccupiedSheds / totalSheds) * 100 : 0;
    
    // Use leased land stats from API
    const totalLeasedLand = leasedLandStats.totalLeasedLand;
    const occupiedLeasedLand = leasedLandStats.occupiedLeasedLand;
    const landLeaseRatio = leasedLandStats.landLeaseRatio;
    
    return { 
      totalParkArea, 
      totalParkAreaM2,
      totalParcelArea,
      totalOccupiedArea, 
      overallOccupancyRate, 
      totalParcels, 
      totalOccupiedParcels, 
      overallParcelOccupancy, 
      totalBuildings, 
      totalActiveBuildings, 
      overallBuildingUtilization, 
      avgGreenAreaRatio, 
      avgRoadRatio, 
      totalSheds, 
      totalOccupiedSheds, 
      overallShedOccupancy,
      totalParks: parksData.length,
      totalGreenArea,
      totalRoadArea,
      totalLeasedLand,
      occupiedLeasedLand,
      landLeaseRatio
    };
  }, [parkMetrics, parksData, leasedLandStats]);

  // Apply filters
  const applyFilters = useCallback(() => {
    let filteredList = [...parksData];
    if (searchTerm) {
      filteredList = filteredList.filter(park => 
        park.name?.toLowerCase().includes(searchTerm.toLowerCase()) || 
        park.region?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        park.specialization?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    if (selectedPark !== "all") {
      filteredList = filteredList.filter(park => park.name === selectedPark);
    }
    setFilteredParks(filteredList);
    setPage(0);
  }, [parksData, searchTerm, selectedPark]);

  useEffect(() => { 
    applyFilters(); 
  }, [applyFilters]);

  // Prepare chart data
  const chartData = useMemo(() => {
    const occupancyData = {}, parcelRatioData = {}, buildingRatioData = {}, greenAreaData = {}, roadAreaData = {}, shedOccupancyData = {}, landLeaseData = {};
    filteredParks.forEach(park => {
      const metrics = parkMetrics[park.name];
      if (metrics) {
        occupancyData[park.name] = { rate: metrics.occupancyRate };
        parcelRatioData[park.name] = { ratio: metrics.parcelRatio };
        buildingRatioData[park.name] = { ratio: metrics.buildingRatio };
        greenAreaData[park.name] = { ratio: metrics.greenAreaRatio };
        roadAreaData[park.name] = { ratio: metrics.roadRatio };
        shedOccupancyData[park.name] = { ratio: metrics.shedOccupancyRate };
        landLeaseData[park.name] = { ratio: metrics.landLeaseRatio };
      }
    });
    return { occupancyData, parcelRatioData, buildingRatioData, greenAreaData, roadAreaData, shedOccupancyData, landLeaseData };
  }, [filteredParks, parkMetrics]);

  // Export to CSV
  const handleExportCSV = () => {
    const headers = [
      'Park Name', 'Region', 'Specialization', 
      'Total Area (ha)', 'Total Area (m²)', 'Total Parcel Area (m²)', 'Industrial Parcel Area (m²)', 
      'Parcel Occupancy (%)', 'Building Area (m²)', 'Building Utilization (%)', 
      'Shed Area (m²)', 'Shed Occupancy (%)', 'Green Area (m²)', 'Green Ratio (%)', 
      'Road Area (m²)', 'Road Ratio (%)', 'Occupancy Rate (%)',
      'Leased Land Area (m²)', 'Occupied Leased Land (m²)', 'Land Lease Ratio (%)'
    ];
    const data = filteredParks.map(park => {
      const metrics = parkMetrics[park.name];
      return [
        park.name, 
        park.region, 
        park.specialization,
        formatNumber(metrics?.totalArea || 0),
        formatNumber(metrics?.totalAreaM2 || 0),
        formatNumber(metrics?.totalParcelArea || 0),
        formatNumber(metrics?.industrialParcelArea || 0),
        formatPercentage(metrics?.parcelRatio || 0),
        formatNumber(metrics?.totalBuildingArea || 0),
        formatPercentage(metrics?.buildingRatio || 0),
        formatNumber(metrics?.totalShedArea || 0),
        formatPercentage(metrics?.shedOccupancyRate || 0),
        formatNumber(metrics?.greenArea || 0),
        formatPercentage(metrics?.greenAreaRatio || 0),
        formatNumber(metrics?.roadArea || 0),
        formatPercentage(metrics?.roadRatio || 0),
        formatPercentage(metrics?.occupancyRate || 0),
        formatNumber(metrics?.totalLeasedLand || 0),
        formatNumber(metrics?.occupiedLeasedLand || 0),
        formatPercentage(metrics?.landLeaseRatio || 0)
      ];
    });
    const csvContent = [headers, ...data].map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `occupancy_report_${format(new Date(), 'yyyyMMdd')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const resetFilters = () => { 
    setSearchTerm(""); 
    setSelectedPark("all"); 
  };
  
  const handleChangePage = (event, newPage) => { setPage(newPage); };
  const handleChangeRowsPerPage = (event) => { 
    setRowsPerPage(parseInt(event.target.value, 10)); 
    setPage(0); 
  };
  
  const getCurrentPageData = () => { 
    return filteredParks.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage); 
  };
  
  const handleViewDetails = (park) => { 
    setSelectedParkDetail(park); 
    setOpenDetailsDialog(true); 
    setBuildingPage(0);
  };

  const renderChart = (title, data, dataKey, color, formatValue) => {
    const hasValidData = Object.values(data).some(item => toNumber(item[dataKey]) > 0);
    if (!hasValidData) {
      return <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="body2" color="textSecondary">No data available</Typography>
      </Box>;
    }
    if (chartType === 'pie') {
      const pieData = {};
      Object.entries(data).forEach(([key, val]) => { 
        const value = toNumber(val[dataKey]); 
        if (value > 0) pieData[key] = value; 
      });
      return <PieChart title={title} data={pieData} formatValue={formatValue} />;
    } else {
      return <BarChart title={title} data={data} dataKey={dataKey} color={color} formatValue={formatValue} />;
    }
  };

  return (
    <Box sx={{ width: '100%', bgcolor: '#f5f5f5', minHeight: '100vh', p: 3 }}>
      <Typography variant="h6" gutterBottom sx={{ color: '#1a237e', fontWeight: 'bold', textAlign: 'center', mb: 1 }}>
        Park Occupancy & Utilization Report
      </Typography>
      <Typography variant="body2" sx={{ textAlign: 'center', color: '#666', mb: 3 }}>
        Comprehensive analysis of park occupancy, vacancy ratios, and land utilization metrics including green areas, road infrastructure, and leased land
      </Typography>
      
      {/* Summary Cards - Includes Leased Land Card */}
      <Grid container spacing={1} sx={{ mb: 2, display: 'flex', justifyContent: 'center' }}>
        <Grid item xs={12} sm={6} md={1.5} sx={{ display: 'flex', justifyContent: 'center' }}>
          <Card sx={{ bgcolor: 'white', boxShadow: 1, width: '100%' }}>
            <CardContent sx={{ textAlign: 'center', py: 1 }}>
              <BusinessIcon sx={{ color: '#4caf50', fontSize: 18 }} />
              <Typography variant="body2" sx={{ color: '#1a237e', fontWeight: 'bold' }}>
                {/* {formatNumber(summaryStats.totalParks)} */}
                13
              </Typography>
              <Typography variant="caption" sx={{ color: '#666' }}>Total Parks</Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={1.5} sx={{ display: 'flex', justifyContent: 'center' }}>
          <Card sx={{ bgcolor: 'white', boxShadow: 1, width: '100%' }}>
            <CardContent sx={{ textAlign: 'center', py: 1 }}>
              <ViewModuleIcon sx={{ color: '#2196f3', fontSize: 18 }} />
              <Typography variant="body2" sx={{ color: '#1a237e', fontWeight: 'bold' }}>
                {formatNumber(summaryStats.totalParcels)}
              </Typography>
              <Typography variant="caption" sx={{ color: '#666' }}>Total Parcels</Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={1.5} sx={{ display: 'flex', justifyContent: 'center' }}>
          <Card sx={{ bgcolor: 'white', boxShadow: 1, width: '100%' }}>
            <CardContent sx={{ textAlign: 'center', py: 1 }}>
              <WarehouseIcon sx={{ color: '#ff9800', fontSize: 18 }} />
              <Typography variant="body2" sx={{ color: '#1a237e', fontWeight: 'bold' }}>
                {formatNumber(summaryStats.totalSheds)}
              </Typography>
              <Typography variant="caption" sx={{ color: '#666' }}>Total Sheds</Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={1.5} sx={{ display: 'flex', justifyContent: 'center' }}>
          <Card sx={{ bgcolor: 'white', boxShadow: 1, width: '100%' }}>
            <CardContent sx={{ textAlign: 'center', py: 1 }}>
              <StraightenIcon sx={{ color: '#9c27b0', fontSize: 18 }} />
              <Typography variant="body2" sx={{ color: '#1a237e', fontWeight: 'bold' }}>
                {formatNumber(summaryStats.totalParkArea)} m²
              </Typography>
              <Typography variant="caption" sx={{ color: '#666' }}>Total Area</Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={1.5} sx={{ display: 'flex', justifyContent: 'center' }}>
          <Card sx={{ bgcolor: 'white', boxShadow: 1, width: '100%' }}>
            <CardContent sx={{ textAlign: 'center', py: 1 }}>
              <MeetingRoomIcon sx={{ color: '#4caf50', fontSize: 18 }} />
              <Typography variant="body2" sx={{ color: '#1a237e', fontWeight: 'bold' }}>
                {formatPercentage(summaryStats.overallOccupancyRate)}
              </Typography>
              <Typography variant="caption" sx={{ color: '#666' }}>Occupancy Rate</Typography>
            </CardContent>
          </Card>
        </Grid>
        
        {/* Leased Land Card - Using API data */}
        <Grid item xs={12} sm={6} md={1.5} sx={{ display: 'flex', justifyContent: 'center' }}>
          <Card sx={{ bgcolor: 'white', boxShadow: 1, width: '100%' }}>
            <CardContent sx={{ textAlign: 'center', py: 1 }}>
              <AgricultureIcon sx={{ color: '#2e7d32', fontSize: 18 }} />
              <Typography variant="body2" sx={{ color: '#1a237e', fontWeight: 'bold' }}>
                {formatNumber(summaryStats.totalLeasedLand)} m²
              </Typography>
              <Typography variant="caption" sx={{ color: '#666' }}>Leased Land</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      
      {/* Filters */}
      <Paper elevation={2} sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by park name, region, or specialization..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (<InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>),
                endAdornment: searchTerm && (<InputAdornment position="end"><ClearIcon sx={{ cursor: 'pointer' }} onClick={() => setSearchTerm("")} /></InputAdornment>)
              }}
            />
          </Grid>
          <Grid item xs={12} md={2.5}>
            <FormControl fullWidth size="small">
              <InputLabel>Park</InputLabel>
              <Select value={selectedPark} onChange={(e) => setSelectedPark(e.target.value)} label="Park">
                {parksList.map((park) => (<MenuItem key={park} value={park}>{park === "all" ? "All Parks" : park}</MenuItem>))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={1.5}>
            <Button fullWidth variant="outlined" onClick={resetFilters} startIcon={<ClearIcon />} size="small">
              Reset
            </Button>
          </Grid>
          <Grid item xs={12} md={1.5}>
            <Button fullWidth variant="contained" color="primary" size="small" onClick={handleExportCSV} startIcon={<DownloadIcon />}>
              Export CSV
            </Button>
          </Grid>
          <Grid item xs={12} md={1.5}>
            <Button fullWidth variant="outlined" size="small" onClick={() => setShowChart(!showChart)}>
              {showChart ? "Hide Chart" : "Show Chart"}
            </Button>
          </Grid>
        </Grid>
      </Paper>
      
      {/* Conditional Rendering: Show Chart OR Table */}
      {showChart ? (
        <Paper elevation={2} sx={{ p: 2, mb: 3, borderRadius: 2 }}>
          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="body2" color="textSecondary">Chart Type:</Typography>
              <ToggleButtonGroup value={chartType} exclusive onChange={(e, val) => val && setChartType(val)} size="small">
                <ToggleButton value="bar">
                  <Tooltip title="Bar Chart"><BarChartIcon fontSize="small" /></Tooltip>
                </ToggleButton>
                <ToggleButton value="pie">
                  <Tooltip title="Pie Chart"><PieChartIcon fontSize="small" /></Tooltip>
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
          </Box>
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
            <Tabs value={chartTab} onChange={(e, newValue) => setChartTab(newValue)}>
              <Tab label="Occupancy Rate" />
              <Tab label="Parcel Occupancy" />
              <Tab label="Building Occupancy" />
              <Tab label="Shed Occupancy" />
              <Tab label="Green Area Ratio" />
              <Tab label="Road Ratio" />
              <Tab label="Land Lease Ratio" />
            </Tabs>
          </Box>
          <Box sx={{ mt: 2, minHeight: 450 }}>
            <TabPanel value={chartTab} index={0}>
              <GaugeChart title="Overall Park Occupancy" value={summaryStats.overallOccupancyRate} color="#4caf50" />
              {renderChart("Occupancy Rate by Park", chartData.occupancyData, "rate", "#4caf50", (v) => formatPercentage(v))}
            </TabPanel>
            <TabPanel value={chartTab} index={1}>
              <GaugeChart title="Overall Parcel Occupancy" value={summaryStats.overallParcelOccupancy} color="#2196f3" />
              {renderChart("Parcel Occupancy by Park", chartData.parcelRatioData, "ratio", "#2196f3", (v) => formatPercentage(v))}
            </TabPanel>
            <TabPanel value={chartTab} index={2}>
              <GaugeChart title="Overall Building Utilization" value={summaryStats.overallBuildingUtilization} color="#ff9800" />
              {renderChart("Building Occupancy by Park", chartData.buildingRatioData, "ratio", "#ff9800", (v) => formatPercentage(v))}
            </TabPanel>
            <TabPanel value={chartTab} index={3}>
              <GaugeChart title="Overall Shed Occupancy" value={summaryStats.overallShedOccupancy} color="#9c27b0" />
              {renderChart("Shed Occupancy by Park", chartData.shedOccupancyData, "ratio", "#9c27b0", (v) => formatPercentage(v))}
            </TabPanel>
            <TabPanel value={chartTab} index={4}>
              <GaugeChart title="Total Green Area Ratio" value={summaryStats.avgGreenAreaRatio} color="#4caf50" />
              {renderChart("Green Area Ratio by Park", chartData.greenAreaData, "ratio", "#4caf50", (v) => formatPercentage(v))}
            </TabPanel>
            <TabPanel value={chartTab} index={5}>
              <GaugeChart title="Total Road Ratio" value={summaryStats.avgRoadRatio} color="#795548" />
              {renderChart("Road Ratio by Park", chartData.roadAreaData, "ratio", "#795548", (v) => formatPercentage(v))}
            </TabPanel>
            <TabPanel value={chartTab} index={6}>
              <GaugeChart title="Land Lease Ratio" value={summaryStats.landLeaseRatio} color="#2e7d32" />
              {renderChart("Land Lease Ratio by Park", chartData.landLeaseData, "ratio", "#2e7d32", (v) => formatPercentage(v))}
            </TabPanel>
          </Box>
        </Paper>
      ) : (
        <Paper elevation={2} sx={{ borderRadius: 2, overflow: 'hidden' }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
              <CircularProgress />
              <Typography sx={{ ml: 2 }}>Loading...</Typography>
            </Box>
          ) : filteredParks.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
              <Typography>No data available</Typography>
            </Box>
          ) : (
            <>
             <TableContainer>
  <Table size="small">
    <TableHead sx={{ bgcolor: '#f5f5f5' }}>
      <TableRow>
        <TableCell sx={{ fontWeight: 'bold' }}>Park Name</TableCell>
        <TableCell align="right" sx={{ fontWeight: 'bold' }}>Total Area (m²)</TableCell>
        <TableCell align="right" sx={{ fontWeight: 'bold', color: 'black' }}>Parcel Area (m²)</TableCell>
        <TableCell align="right" sx={{ fontWeight: 'bold', color: 'black' }}>Parcel Occup.</TableCell>
        <TableCell align="right" sx={{ fontWeight: 'bold', color: 'black' }}>Building Area (m²)</TableCell>
        {/* <TableCell align="right" sx={{ fontWeight: 'bold', color: 'black' }}>Building Util.</TableCell> */}
        <TableCell align="right" sx={{ fontWeight: 'bold', color: 'black' }}>Shed Area (m²)</TableCell>
        <TableCell align="right" sx={{ fontWeight: 'bold', color: 'black' }}>Shed Occup.</TableCell>
        <TableCell align="right" sx={{ fontWeight: 'bold', color: 'black' }}>Leased Land (m²)</TableCell>
        <TableCell align="right" sx={{ fontWeight: 'bold', color: 'black' }}>Land Lease Ratio</TableCell>
        <TableCell align="right" sx={{ fontWeight: 'bold', color: 'black' }}>Green Area (m²)</TableCell>
        <TableCell align="right" sx={{ fontWeight: 'bold', color: 'black' }}>Green Ratio</TableCell>
        <TableCell align="right" sx={{ fontWeight: 'bold', color: 'black' }}>Road Area (m²)</TableCell>
        <TableCell align="right" sx={{ fontWeight: 'bold', color: 'black' }}>Road Ratio</TableCell>
        <TableCell align="right" sx={{ fontWeight: 'bold', color: 'black' }}>Occupancy</TableCell>
      </TableRow>
    </TableHead>
    <TableBody>
      {getCurrentPageData().map((park) => {
        const metrics = parkMetrics[park.name];
        if (!metrics) return null;
        
        return (
          <TableRow key={park.id} hover>
            <TableCell sx={{ fontWeight: 'medium', cursor: 'pointer' }} onClick={() => handleViewDetails(park)}>
              {park.name}
            </TableCell>
            <TableCell align="right">{formatNumber(metrics.totalArea)}</TableCell>
            <TableCell align="right">{formatNumber(metrics.totalParcelArea)}</TableCell>
            <TableCell align="right">
              <ProgressBarCell value={metrics.parcelRatio} label="Parcel Occupancy" actualValue={metrics.parcelRatio} color="#795548" />
            </TableCell>
            <TableCell align="right">{formatNumber(metrics.totalBuildingArea)}</TableCell>
            {/* <TableCell align="right">
              <ProgressBarCell value={metrics.buildingRatio} label="Building Utilization" actualValue={metrics.buildingRatio} color="#4caf50" />
            </TableCell> */}
            {/* Shed Area Column */}
            <TableCell align="right">{formatNumber(metrics.totalShedArea)}</TableCell>
            {/* Shed Occupancy Column - This is the fix! */}
            <TableCell align="right">
              <ProgressBarCell value={metrics.shedOccupancyRate} label="Shed Occupancy" actualValue={metrics.shedOccupancyRate} color="#795548" />
            </TableCell>
            {/* Leased Land Column */}
            <TableCell align="right" sx={{ fontWeight: 'bold', color: '#2e7d32' }}>
              {formatNumber(metrics.totalLeasedLand)}
            </TableCell>
            {/* Land Lease Ratio Column */}
            <TableCell align="right">
              <ProgressBarCell value={metrics.landLeaseRatio} label="Land Lease Ratio" actualValue={metrics.landLeaseRatio} color="#2e7d32" />
            </TableCell>
            {/* Green Area Column */}
            <TableCell align="right">{formatNumber(metrics.greenArea)}</TableCell>
            {/* Green Ratio Column */}
            <TableCell align="right">
              <ProgressBarCell value={metrics.greenAreaRatio} label="Green Area Ratio" actualValue={metrics.greenAreaRatio} color="#4caf50" />
            </TableCell>
            {/* Road Area Column */}
            <TableCell align="right">{formatNumber(metrics.roadArea)}</TableCell>
            {/* Road Ratio Column */}
            <TableCell align="right">
              <ProgressBarCell value={metrics.roadRatio} label="Road Ratio" actualValue={metrics.roadRatio} color="#795548" />
            </TableCell>
            {/* Occupancy Column */}
            <TableCell align="right">
              <ProgressBarCell value={metrics.occupancyRate} label="Occupancy Rate" actualValue={metrics.occupancyRate} color="#4caf50" />
            </TableCell>
          </TableRow>
        );
      })}
    </TableBody>
  </Table>
</TableContainer>
              <TablePagination 
                rowsPerPageOptions={[5, 10, 25, 50]} 
                component="div" 
                count={filteredParks.length} 
                rowsPerPage={rowsPerPage} 
                page={page} 
                onPageChange={handleChangePage} 
                onRowsPerPageChange={handleChangeRowsPerPage} 
              />
            </>
          )}
        </Paper>
      )}
      
      {/* Details Dialog - Updated with Leased Land Metrics and Parcel vs Shed Ratio */}
      <Dialog open={openDetailsDialog} onClose={() => setOpenDetailsDialog(false)} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#1a237e' }}>
              Park Details - {selectedParkDetail?.name}
            </Typography>
            <IconButton onClick={() => setOpenDetailsDialog(false)}><CloseIcon /></IconButton>
          </Box>
        </DialogTitle>
        <Divider />
        <DialogContent>
          {selectedParkDetail && (
            <Box sx={{ mt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" sx={{ color: '#666' }}>Park Name</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 'medium' }}>{selectedParkDetail.name}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" sx={{ color: '#666' }}>Region</Typography>
                  <Typography variant="body1">{selectedParkDetail.region}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" sx={{ color: '#666' }}>Specialization</Typography>
                  <Typography variant="body1">{selectedParkDetail.specialization}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" sx={{ color: '#666' }}>Total Area</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                    {formatNumber(selectedParkDetail.area_on_plan)} ha ({formatNumber(selectedParkDetail.area_on_plan * 10000)} m²)
                  </Typography>
                </Grid>
                
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#1a237e', mb: 2 }}>Buildings in this Park</Typography>
                  {(() => {
                    const parkBuildings = getBuildingsForPark(selectedParkDetail.id);
                    if (parkBuildings.length === 0) {
                      return (
                        <Box sx={{ p: 2, textAlign: 'center', bgcolor: '#f5f5f5', borderRadius: 1 }}>
                          <Typography variant="body2" color="textSecondary">No buildings found for this park</Typography>
                        </Box>
                      );
                    }
                    
                    const displayedBuildings = parkBuildings.slice(
                      buildingPage * buildingRowsPerPage,
                      buildingPage * buildingRowsPerPage + buildingRowsPerPage
                    );
                    
                    return (
                      <>
                        <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
                          <Table size="small">
                            <TableHead sx={{ bgcolor: '#f5f5f5' }}>
                              <TableRow>
                                <TableCell sx={{ fontWeight: 'bold' }}>Building ID</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Building Name</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Building No.</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 'bold' }}>Floor Area (m²)</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 'bold' }}>No. of Floors</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 'bold' }}>No. of Units</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 'bold' }}>Occupancy Ratio (%)</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {displayedBuildings.map((building) => (
                                <TableRow key={building.id} hover>
                                  <TableCell>{building.id}</TableCell>
                                  <TableCell>{building.name}</TableCell>
                                  <TableCell>{building.bldg_no}</TableCell>
                                  <TableCell align="right" sx={{ fontWeight: 'bold', color: '#ff9800' }}>
                                    <Tooltip title={`${formatNumber(building.floor_area_m2)} m²`}>
                                      <span>{formatNumber(building.floor_area_m2)}</span>
                                    </Tooltip>
                                  </TableCell>
                                  <TableCell align="right">{building.no_of_floors}</TableCell>
                                  <TableCell align="right">{building.no_of_units}</TableCell>
                                  <TableCell align="right">
                                    <ProgressBarCell 
                                      value={building.occupancy_ratio} 
                                      label="Occupancy Ratio" 
                                      actualValue={building.occupancy_ratio}
                                      color="#4caf50"
                                    />
                                  </TableCell>
                                  <TableCell>
                                    <Typography 
                                      variant="body2" 
                                      sx={{ 
                                        color: building.status === 'Active' ? '#4caf50' : '#ff9800',
                                        fontWeight: 'medium'
                                      }}
                                    >
                                      {building.status}
                                    </Typography>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                        <TablePagination
                          rowsPerPageOptions={[5, 10, 25]}
                          component="div"
                          count={parkBuildings.length}
                          rowsPerPage={buildingRowsPerPage}
                          page={buildingPage}
                          onPageChange={(e, newPage) => setBuildingPage(newPage)}
                          onRowsPerPageChange={(e) => {
                            setBuildingRowsPerPage(parseInt(e.target.value, 10));
                            setBuildingPage(0);
                          }}
                        />
                      </>
                    );
                  })()}
                </Grid>
                
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#1a237e', mb: 2 }}>Infrastructure Metrics</Typography>
                </Grid>
                
                {(() => {
                  const metrics = parkMetrics[selectedParkDetail.name];
                  if (!metrics) return null;
                  
                  const parcelVsShedRatio = metrics.totalShedArea > 0 ? (metrics.totalParcelArea / metrics.totalShedArea) : 0;
                  
                  return (
                    <>
                      <Grid item xs={12} md={4}>
                        <Paper variant="outlined" sx={{ p: 2, bgcolor: '#e3f2fd' }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#1565c0' }}>Parcel</Typography>
                          <Typography variant="body2">Total Parcel Area: {formatNumber(metrics.totalParcelArea)} m²</Typography>
                          <Typography variant="body2">Industrial Area: {formatNumber(metrics.industrialParcelArea)} m²</Typography>
                          <Typography variant="body2">Occupied: {formatNumber(metrics.occupiedIndustrialArea)} m²</Typography>
                          <Box sx={{ mt: 1 }}>
                            <LinearProgress 
                              variant="determinate" 
                              value={metrics.parcelRatio} 
                              sx={{ height: 6, borderRadius: 3, backgroundColor: '#bbdefb', '& .MuiLinearProgress-bar': { backgroundColor: '#2196f3' } }}
                            />
                            <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#2196f3', mt: 0.5 }}>
                              Occupancy: {formatPercentage(metrics.parcelRatio)}
                            </Typography>
                          </Box>
                        </Paper>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <Paper variant="outlined" sx={{ p: 2, bgcolor: '#fff3e0' }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#ef6c00' }}>Building</Typography>
                          <Typography variant="body2">Area: {formatNumber(metrics.totalBuildingArea)} m²</Typography>
                          <Typography variant="body2">Active: {formatNumber(metrics.activeBuildingArea)} m²</Typography>
                          <Typography variant="body2">Buildings: {metrics.totalBuildings}</Typography>
                          <Box sx={{ mt: 1 }}>
                            <LinearProgress 
                              variant="determinate" 
                              value={metrics.buildingRatio} 
                              sx={{ height: 6, borderRadius: 3, backgroundColor: '#ffe0b2', '& .MuiLinearProgress-bar': { backgroundColor: '#ff9800' } }}
                            />
                            <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#ff9800', mt: 0.5 }}>
                              Utilization: {formatPercentage(metrics.buildingRatio)}
                            </Typography>
                          </Box>
                        </Paper>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <Paper variant="outlined" sx={{ p: 2, bgcolor: '#f3e5f5' }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#7b1fa2' }}>Shed</Typography>
                          <Typography variant="body2">Area: {formatNumber(metrics.totalShedArea)} m²</Typography>
                          <Typography variant="body2">Occupied: {formatNumber(metrics.occupiedShedArea)} m²</Typography>
                          <Box sx={{ mt: 1 }}>
                            <LinearProgress 
                              variant="determinate" 
                              value={metrics.shedOccupancyRate} 
                              sx={{ height: 6, borderRadius: 3, backgroundColor: '#e1bee7', '& .MuiLinearProgress-bar': { backgroundColor: '#9c27b0' } }}
                            />
                            <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#9c27b0', mt: 0.5 }}>
                              Occupancy: {formatPercentage(metrics.shedOccupancyRate)}
                            </Typography>
                          </Box>
                        </Paper>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <Paper variant="outlined" sx={{ p: 2, bgcolor: '#c8e6c9' }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#2e7d32' }}>Green Area</Typography>
                          <Typography variant="body2">Total Green Area: {formatNumber(metrics.greenArea)} m²</Typography>
                          <Typography variant="body2">Green Spaces: {greenAreasData.filter(g => toNumber(g.park_id) === toNumber(selectedParkDetail.id)).length}</Typography>
                          <Box sx={{ mt: 1 }}>
                            <LinearProgress 
                              variant="determinate" 
                              value={metrics.greenAreaRatio} 
                              sx={{ height: 6, borderRadius: 3, backgroundColor: '#a5d6a7', '& .MuiLinearProgress-bar': { backgroundColor: '#4caf50' } }}
                            />
                            <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#4caf50', mt: 0.5 }}>
                              Ratio: {formatPercentage(metrics.greenAreaRatio)}
                            </Typography>
                          </Box>
                        </Paper>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <Paper variant="outlined" sx={{ p: 2, bgcolor: '#d7ccc8' }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#5d4037' }}>Road Area</Typography>
                          <Typography variant="body2">Total Road Area: {formatNumber(metrics.roadArea)} m²</Typography>
                          <Typography variant="body2">Road Segments: {roadsData.filter(r => toNumber(r.park_id) === toNumber(selectedParkDetail.id)).length}</Typography>
                          <Box sx={{ mt: 1 }}>
                            <LinearProgress 
                              variant="determinate" 
                              value={metrics.roadRatio} 
                              sx={{ height: 6, borderRadius: 3, backgroundColor: '#bcaaa4', '& .MuiLinearProgress-bar': { backgroundColor: '#795548' } }}
                            />
                            <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#795548', mt: 0.5 }}>
                              Ratio: {formatPercentage(metrics.roadRatio)}
                            </Typography>
                          </Box>
                        </Paper>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <Paper variant="outlined" sx={{ p: 2, bgcolor: '#e8f5e9' }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#2e7d32' }}>Leased Land</Typography>
                          <Typography variant="body2">Total Leased Land: {formatNumber(metrics.totalLeasedLand)} m²</Typography>
                          <Typography variant="body2">Occupied Leased Land: {formatNumber(metrics.occupiedLeasedLand)} m²</Typography>
                          <Box sx={{ mt: 1 }}>
                            <LinearProgress 
                              variant="determinate" 
                              value={metrics.landLeaseRatio} 
                              sx={{ height: 6, borderRadius: 3, backgroundColor: '#c8e6c9', '& .MuiLinearProgress-bar': { backgroundColor: '#2e7d32' } }}
                            />
                            <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#2e7d32', mt: 0.5 }}>
                              Land Lease Ratio: {formatPercentage(metrics.landLeaseRatio)}
                            </Typography>
                          </Box>
                        </Paper>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Paper variant="outlined" sx={{ p: 2, bgcolor: '#e1f5fe' }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#0277bd' }}>Parcel vs Shed Ratio</Typography>
                          <Typography variant="body2">Total Parcel Area: {formatNumber(metrics.totalParcelArea)} m²</Typography>
                          <Typography variant="body2">Total Shed Area: {formatNumber(metrics.totalShedArea)} m²</Typography>
                          <Typography variant="h4" sx={{ fontWeight: 'bold', color: parcelVsShedRatio > 1 ? '#4caf50' : '#ff9800', mt: 1 }}>
                            {parcelVsShedRatio.toFixed(2)} : 1
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#666' }}>
                            {parcelVsShedRatio > 1 ? 'More parcel area than shed area' : 'More shed area than parcel area'}
                          </Typography>
                          <Box sx={{ mt: 2 }}>
                            <LinearProgress 
                              variant="determinate" 
                              value={Math.min(100, (metrics.totalShedArea / metrics.totalParcelArea) * 100)} 
                              sx={{ height: 8, borderRadius: 4, backgroundColor: '#b3e5fc', '& .MuiLinearProgress-bar': { backgroundColor: '#0288d1' } }}
                            />
                            <Typography variant="body2" sx={{ mt: 0.5 }}>
                              Shed area is {((metrics.totalShedArea / metrics.totalParcelArea) * 100).toFixed(1)}% of Parcel area
                            </Typography>
                          </Box>
                        </Paper>
                      </Grid>
                    </>
                  );
                })()}
              </Grid>
            </Box>
          )}
        </DialogContent>
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <Button onClick={() => setOpenDetailsDialog(false)} variant="outlined">Close</Button>
        </Box>
      </Dialog>
    </Box>
  );
};

export default OccupancyReportDashboard;